import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../SEO/SEO';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { MessageSquare, AlertCircle, Phone, Mail, MapPin, Clock, Shield, CheckCircle2, ArrowRight, Building2, User, ExternalLink, X, FileText } from 'lucide-react';
import './GrievancesRedressal.css';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import * as LucideIcons from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
const GrievanceRedressal = () => {
  // State for popup modal
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const navigate = useNavigate();
  // Banner state
  const [banner, setBanner] = useState(null);
  const [loading, setLoading] = useState(true);

  // New states for dynamic content
  const [steps, setSteps] = useState([]);
  const [stepsSettings, setStepsSettings] = useState({
    section_title: 'Three-Step Resolution Process',
    section_subtitle: 'Clear pathways to resolve your concerns'
  });
  const [complaintMethods, setComplaintMethods] = useState([]);
  const [escalationLevels, setEscalationLevels] = useState([]);
  const [customerRights, setCustomerRights] = useState([]);
  const [commitmentSettings, setCommitmentSettings] = useState({
    title: 'Our Commitment to Fair and Transparent Service',
    description: 'We are committed to treating every customer with dignity, fairness, and respect. Your feedback helps us improve and ensures that we continue to deliver responsible and customer-focused financial services.',
    promise_title: 'Our Promise',
    promise_text: 'We review all customer grievances with care and work towards resolution in a time-bound and transparent manner.'
  });
  const [methodSettings, setMethodSettings] = useState({
    section_title: 'How to Raise a Complaint',
    section_description: 'If you have any concerns, queries, or complaints regarding our services, we encourage you to share them with us. You may register your grievance at the branch where your loan was availed.',
    badge_text: 'Level 1',
    footer_text: 'We aim to acknowledge and address your complaint at the branch level within 15 working days.'
  });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  const baseUrl = backendUrl.endsWith('/api') ? backendUrl.replace('/api', '') : backendUrl;
  const apiBase = backendUrl.endsWith('/api') ? backendUrl : `${backendUrl}/api`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch everything in parallel
        const [
          bannerRes, stepsRes, stepsSettingsRes,
          methodsRes, escalationRes, rightsRes, commitmentRes, methodSettingsRes
        ] = await Promise.all([
          api.get('/banners/active?page_type=grievance_redressal'),
          api.get('/admin/grievance-process/active-steps'),
          api.get('/admin/grievance-process/settings'),
          api.get('/admin/grievance-complaint/methods/active'),
          api.get('/admin/grievance-escalation/levels/active'),
          api.get('/admin/grievance-customer-rights/active'),
          api.get('/admin/grievance-commitment/settings'),
          api.get('/admin/grievance-complaint/methods/settings')
        ]);

        const bannerData = bannerRes.data;
        const stepsData = stepsRes.data;
        const stepsSettingsData = stepsSettingsRes.data;
        const methodsData = methodsRes.data;
        const escalationData = escalationRes.data;
        const rightsData = rightsRes.data;
        const commitmentData = commitmentRes.data;
        const methodSettingsData = methodSettingsRes.data;

        if (bannerData.success && bannerData.data?.length > 0) {
          const activeBanner = bannerData.data.find(b => b.is_active);
          if (activeBanner) setBanner(activeBanner);
        }

        if (stepsData.success) setSteps(stepsData.data || []);
        if (stepsSettingsData.success && stepsSettingsData.data) setStepsSettings(stepsSettingsData.data);
        if (methodsData.success) setComplaintMethods(methodsData.data || []);
        if (escalationData.success) setEscalationLevels(escalationData.data || []);
        if (rightsData.success) setCustomerRights((rightsData.data || []).map(r => r.right_text));
        if (commitmentData.success && commitmentData.data) setCommitmentSettings(commitmentData.data);
        if (methodSettingsData.success && methodSettingsData.data) setMethodSettings(methodSettingsData.data);

      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);


  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    loanAccountNumber: '',
    productType: '',
    branchLocation: '',
    grievanceNature: '',
    grievanceDescription: '',
    issueDate: '',
    consent: false
  });

  // OTP state
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Timer for OTP resend
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Close popup on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        handleClosePopup();
      }
    };
    if (isPopupOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isPopupOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Reset OTP verification if phone number changes
    if (name === 'phone') {
      setOtpSent(false);
      setOtpVerified(false);
      setOtp('');
      setOtpError('');
    }
  };

  const handleSendOtp = async () => {
    if (formData.phone.length !== 10) {
      setOtpError('Please enter a valid 10-digit phone number');
      return;
    }

    setOtpLoading(true);
    setOtpError('');

    try {
      const response = await fetch(`${apiBase}/otp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone: formData.phone }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setOtpSent(true);
        setResendTimer(60);
        toast.success('OTP sent successfully!');
      } else {
        setOtpError(data.message || 'Failed to send OTP. Please try again.');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      setOtpError('Failed to send OTP. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setVerifyLoading(true);
    setOtpError('');

    try {
      const response = await fetch(`${apiBase}/otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone: formData.phone, otp }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setOtpVerified(true);
        setOtpError('');
      } else {
        setOtpError(data.message || 'Invalid OTP. Please check and try again.');
      }
    } catch (error) {
      setOtpError('An error occurred during verification.');
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!otpVerified) {
      setOtpError('Please verify your phone number first');
      return;
    }

    try {
      // Submit to backend API
      const response = await fetch(`${apiBase}/grievance/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          loanAccountNumber: formData.loanAccountNumber,
          productType: formData.productType,
          branchLocation: formData.branchLocation,
          grievanceNature: formData.grievanceNature,
          grievanceDescription: formData.grievanceDescription,
          issueDate: formData.issueDate
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success('Grievance submitted successfully!');
        // Close form popup and redirect to thank you page
        setIsPopupOpen(false);
        navigate('/thank-you');
      } else {
        setOtpError(data.message || 'Failed to submit grievance. Please try again.');
      }
    } catch (error) {
      console.error('Error submitting grievance:', error);
      setOtpError('Failed to submit grievance. Please try again.');
    }
  };

  const handleClosePopup = () => {
    setIsPopupOpen(false);
    // Reset form
    setFormData({
      name: '',
      phone: '',
      email: '',
      loanAccountNumber: '',
      productType: '',
      branchLocation: '',
      grievanceNature: '',
      grievanceDescription: '',
      issueDate: '',
      consent: false
    });
    setOtp('');
    setOtpSent(false);
    setOtpVerified(false);
    setOtpError('');
  };

  const openComplaintPopup = () => {
    setIsPopupOpen(true);
  };

  // Level 1 logic
  const renderMethod = (method, index) => {
    const IconComponent = LucideIcons[method.icon_name] || LucideIcons.HelpCircle;

    if (method.type === 'link') {
      return (
        <a key={index} href={method.href} className="method-card-link" style={{ textDecoration: 'none' }}>
          <Card className="method-card" style={{ cursor: 'pointer' }}>
            <div className="method-icon"><IconComponent className="w-6 h-6" /></div>
            <h3 className="method-title">{method.title}</h3>
            <p className="method-description">{method.description}</p>
          </Card>
        </a>
      );
    } else {
      return (
        <Card key={index} className="method-card" style={{ cursor: 'pointer' }} onClick={openComplaintPopup}>
          <div className="method-icon"><IconComponent className="w-6 h-6" /></div>
          <h3 className="method-title">{method.title}</h3>
          <p className="method-description">{method.description}</p>
        </Card>
      );
    }
  };

  const lvl2 = escalationLevels.find(l => l.level_number === 2);
  const lvl3 = escalationLevels.find(l => l.level_number === 3);


  return (
    <div className="grievance-page">

      <SEO
        title="Grievance Redressal: Resolve Your Issues | Aham Housing Finance"
        description="Need help with your home loan? View Aham Housing Finance’s Grievance Redressal mechanism. Track your complaint status and get time-bound resolutions. Contact us!"
        keywords="Aham Housing Finance grievances, Aham grievance redressal, customer grievance Aham, home loan complaint, housing finance grievance redressal, grievance redressal officer, loan complaint resolution, customer complaints home loan, branch complaint register, home loan customer support, grievance escalation process, complaint redressal housing finance, Aham customer service complaint, grievance officer Aham, National Housing Bank complaint, NHB complaint housing finance, home loan dispute resolution, loan service complaint, customer grievance process, Aham complaints"
        canonical="https://ahamhfc.com/grievance-redressal-policy-customer-support"
        ogTitle="Grievance Redressal: Resolve Your Issues | Aham Housing Finance"
        ogDescription="Need help with your home loan? View Aham Housing Finance’s Grievance Redressal mechanism. Track your complaint status and get time-bound resolutions. Contact us!"
      />

      {/* Hero Banner */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('${banner?.image_url
              ? (banner.image_url.startsWith('http') || banner.image_url.startsWith('/img/')
                ? banner.image_url
                : `${baseUrl}${banner.image_url.startsWith('/') ? '' : '/'}${banner.image_url}`)
              : '/img/Banners/Grievance-Banner.webp'
              }')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-900/95 via-primary-800/80 to-transparent"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 py-12 md:py-20 text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl text-left"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white mb-6 border border-white/20">
              <Shield className="w-5 h-5" />
              <span className="font-medium">Grievance Redressal</span>
            </div>
            <h1 className="text-3xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
              {banner?.title || 'Grievance Redressal'}
            </h1>
            <p className="text-lg md:text-3xl text-white/90 font-medium max-w-4xl leading-relaxed mb-8">
              {banner?.subtitle || 'We are committed to addressing your concerns with transparency, fairness, and timely resolution.'}
            </p>

            {/* Breadcrumb Navigation */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              className="flex items-center space-x-2 mb-6 md:mb-8"
            >
              <Link
                to="/"
                className="text-white/80 hover:text-white transition-colors duration-200 text-sm font-medium"
              >
                Home
              </Link>
              <ChevronRightIcon className="h-4 w-4 text-white/60" />
              <span className="text-yellow-300 text-sm font-semibold">Grievances</span>
            </motion.div>

          </motion.div>
        </div>
      </section>

      {/* Process Overview */}
      <section className="process-overview">
        <div className="section-container">
          <div className="overview-header text-center lg:text-left">
            <h2 className="overview-title mx-auto lg:mx-0">{stepsSettings.section_title}</h2>
            <p className="overview-subtitle mx-auto lg:mx-0">{stepsSettings.section_subtitle}</p>
          </div>

          <div className="process-steps">
            {steps.length > 0 ? (
              steps.map((step, index) => {
                const IconComponent = LucideIcons[step.icon] || LucideIcons.Building2;
                return (
                  <React.Fragment key={step.id}>
                    <div className="process-step">
                      <div className="step-number">{step.step_number}</div>
                      <div className={`step-icon-wrapper level-${(index % 3) + 1}`}>
                        <IconComponent className="step-icon" />
                      </div>
                      <h3 className="step-title">{step.title}</h3>
                      <p className="step-desc">{step.description}</p>
                      <div className="step-timeline">
                        <Clock className="w-4 h-4" />
                        <span>{step.timeline}</span>
                      </div>
                    </div>
                    {/* Add arrow if not the last item */}
                    {index < steps.length - 1 && (
                      <div className="process-arrow">
                        <ArrowRight className="arrow-icon" />
                      </div>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <div className="text-center w-full py-10">Loading steps...</div>
            )}
          </div>
        </div>
      </section>

      {/* Section 1: Branch Level */}
      <section className="branch-section">
        <div className="section-container">
          <div className="section-header-centered text-center lg:text-left">
            <div className="level-badge level-1-badge mx-auto lg:mx-0">
              <span className="badge-number">{methodSettings.badge_text || 'Level 1'}</span>
            </div>
            <h2 className="section-main-heading mx-auto lg:mx-0">{methodSettings.section_title || 'How to Raise a Complaint'}</h2>
            <p className="section-lead-text mx-auto lg:mx-0">
              {methodSettings.section_description || 'If you have any concerns, queries, or complaints regarding our services, we encourage you to share them with us. You may register your grievance at the branch where your loan was availed.'}
            </p>
          </div>

          <div className="methods-grid">
            {complaintMethods.length > 0 ? (
              complaintMethods.map((method, index) => renderMethod(method, index))
            ) : (
              <div className="text-center w-full py-10 opacity-50">No complaint methods available</div>
            )}
          </div>

          <div className="resolution-info">
            <AlertCircle className="info-icon" />
            <p className="info-text">
              {methodSettings.footer_text || 'We aim to acknowledge and address your complaint at the branch level within 15 working days.'}
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: Grievance Officer */}
      {lvl2 && (
        <section className="officer-section">
          <div className="section-container">
            <div className="officer-layout">
              <div className="officer-content text-center lg:text-left">
                <div className="level-badge level-2-badge mx-auto lg:mx-0">
                  <span className="badge-number">Level 2</span>
                </div>
                <h2 className="section-main-heading mx-auto lg:mx-0">{lvl2.section_title}</h2>
                <p className="section-lead-text mx-auto lg:mx-0">
                  {lvl2.section_description}
                </p>
              </div>

              <Card className="contact-card primary-contact">
                <div className="contact-header">
                  <div className="contact-avatar">
                    <User className="w-8 h-8" />
                  </div>
                  <div className="contact-info">
                    <h3 className="contact-name">{lvl2.officer_name}</h3>
                    <p className="contact-designation">{lvl2.officer_designation}</p>
                  </div>
                </div>

                <div className="contact-details">
                  <div className="contact-item">
                    <Phone className="contact-item-icon" />
                    <div className="contact-item-content">
                      <span className="contact-label">Phone</span>
                      <a href={`tel:${lvl2.phone}`} className="contact-value">{lvl2.phone}</a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <Mail className="contact-item-icon" />
                    <div className="contact-item-content">
                      <span className="contact-label">Email</span>
                      <a href={`mailto:${lvl2.email}`} className="contact-value">{lvl2.email}</a>
                    </div>
                  </div>
                  <div className="contact-item">
                    <Clock className="contact-item-icon" />
                    <div className="contact-item-content">
                      <span className="contact-label">Resolution Time</span>
                      <span className="contact-value-highlight">{lvl2.resolution_time}</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Section 3: NHB */}
      {lvl3 && (
        <section className="nhb-section">
          <div className="section-container">
            <div className="section-header-centered text-center lg:text-left">
              <div className="level-badge level-3-badge mx-auto lg:mx-0">
                <span className="badge-number">Level 3</span>
              </div>
              <h2 className="section-main-heading mx-auto lg:mx-0">{lvl3.section_title}</h2>
              <p className="section-lead-text mx-auto lg:mx-0">
                {lvl3.section_description}
              </p>
            </div>

            <div className="nhb-options">
              <Card className="nhb-option-card online">
                <div className="option-header">
                  <div className="option-icon">
                    <ExternalLink className="w-6 h-6" />
                  </div>
                  <h3 className="option-title">Online (Preferred)</h3>
                </div>
                <p className="option-description">Lodge your complaint through the official NHB grievance portal</p>
                <a
                  href={lvl3.online_portal_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="option-link"
                >
                  {lvl3.online_portal_url}
                  <ExternalLink className="w-4 h-4" />
                </a>
              </Card>

              <Card className="nhb-option-card postal">
                <div className="option-header">
                  <div className="option-icon">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <h3 className="option-title">By Writing To</h3>
                </div>
                <p className="option-description">Send your complaint to the following address:</p>
                <div className="address-block">
                  <div className="address-line text-sm whitespace-pre-line font-medium leading-relaxed">
                    {lvl3.postal_address}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* Section 4: Customer Rights */}
      <section className="rights-section">
        <div className="section-container">
          <div className="rights-layout">
            <div className="rights-content text-center lg:text-left">
              <h2 className="section-main-heading mx-auto lg:mx-0">{commitmentSettings.title}</h2>
              <p className="commitment-text mx-auto lg:mx-0">
                {commitmentSettings.description}
              </p>

              <h3 className="rights-subheading mx-auto lg:mx-0">As a Customer, You Have the Right To:</h3>
              <ul className="rights-list mx-auto lg:mx-0">
                {customerRights.map((right, index) => (
                  <li key={index} className="rights-item text-left justify-start">
                    <CheckCircle2 className="rights-icon" />
                    <span>{right}</span>
                  </li>
                ))}
              </ul>
            </div>

            <Card className="promise-card">
              <div className="promise-icon-wrapper">
                <Shield className="promise-icon" />
              </div>
              <h3 className="promise-title">{commitmentSettings.promise_title}</h3>
              <p className="promise-text">
                {commitmentSettings.promise_text}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Complaint Form Popup Modal */}
      <AnimatePresence>
        {isPopupOpen && (
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={(e) => e.target === e.currentTarget && handleClosePopup()}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[80vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Submit Complaint</h3>
                    <p className="text-sm text-gray-500">We'll address your concern promptly</p>
                  </div>
                </div>
                <button
                  onClick={handleClosePopup}
                  className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              {/* Modal Body - Form */}
              <form onSubmit={handleSubmit} className="p-4 space-y-3">
                {/* Customer Name Field */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Customer Name <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500">Applicant / Borrower name</p>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter applicant / borrower name"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                {/* Registered Mobile Number with OTP */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Registered Mobile Number <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500">Primary contact for updates</p>
                  <div className="flex gap-2">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      maxLength={10}
                      placeholder="Enter 10-digit mobile number"
                      disabled={otpVerified}
                      className={`flex-1 px-4 py-2.5 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none ${otpVerified ? 'bg-green-50 border-green-300' : 'border-gray-300'
                        }`}
                    />
                    {!otpVerified && (
                      <button
                        type="button"
                        onClick={handleSendOtp}
                        disabled={otpLoading || formData.phone.length !== 10 || resendTimer > 0}
                        className={`px-4 py-2.5 rounded-lg font-medium transition-all whitespace-nowrap ${otpLoading || formData.phone.length !== 10 || resendTimer > 0
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-primary-600 text-white hover:bg-primary-700'
                          }`}
                      >
                        {otpLoading ? 'Sending...' : resendTimer > 0 ? `Resend (${resendTimer}s)` : otpSent ? 'Resend OTP' : 'Send OTP'}
                      </button>
                    )}
                  </div>

                  {otpVerified && (
                    <div className="flex items-center gap-2 text-green-600 text-sm">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Phone number verified</span>
                    </div>
                  )}
                </div>

                {/* OTP Input Field */}
                {otpSent && !otpVerified && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-blue-50 p-3 rounded-lg border border-blue-200"
                  >
                    <label className="block text-sm font-medium text-blue-800 mb-2">
                      Enter OTP sent to your mobile
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6-digit OTP"
                        maxLength={6}
                        className="flex-1 p-2.5 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-600 transition text-center tracking-widest font-mono"
                      />
                      <button
                        type="button"
                        onClick={handleVerifyOtp}
                        disabled={verifyLoading || otp.length !== 6}
                        className={`px-4 py-2.5 rounded-lg font-medium transition-all ${verifyLoading || otp.length !== 6
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                          }`}
                      >
                        {verifyLoading ? 'Verifying...' : 'Verify'}
                      </button>
                    </div>
                  </motion.div>
                )}

                {/* OTP Error Message */}
                {otpError && (
                  <div className="flex items-center gap-2 text-red-500 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{otpError}</span>
                  </div>
                )}

                {/* Email ID Field */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Email ID <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500">Acknowledgement & communication</p>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your email address"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                {/* Loan Account Number / Customer ID */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Loan Account Number / Customer ID <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500">Critical for mapping the grievance</p>
                  <input
                    type="text"
                    name="loanAccountNumber"
                    value={formData.loanAccountNumber}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter loan account number or customer ID"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                {/* Product Type */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Product Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="productType"
                    value={formData.productType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none bg-white"
                  >
                    <option value="">Select product type</option>
                    <option value="Home Loan">Home Loan</option>
                    <option value="LAP">LAP (Loan Against Property)</option>
                    <option value="Others">Others</option>
                  </select>
                </div>

                {/* Branch / Location */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Branch / Location <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500">Operational ownership & routing</p>
                  <input
                    type="text"
                    name="branchLocation"
                    value={formData.branchLocation}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter branch name or location"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                {/* Nature of Grievance */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Nature of Grievance <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="grievanceNature"
                    value={formData.grievanceNature}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none bg-white"
                  >
                    <option value="">Select nature of grievance</option>
                    <option value="Disbursement">Disbursement</option>
                    <option value="EMI">EMI</option>
                    <option value="Charges">Charges</option>
                    <option value="SOA">SOA (Statement of Account)</option>
                    <option value="Documents">Documents</option>
                    <option value="CIBIL">CIBIL</option>
                    <option value="Others">Others</option>
                  </select>
                </div>

                {/* Grievance Description */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Grievance Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="grievanceDescription"
                    value={formData.grievanceDescription}
                    onChange={handleInputChange}
                    required
                    rows={3}
                    placeholder="Please describe your complaint or concern in detail..."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none resize-none"
                  />
                </div>

                {/* Date of Issue / Incident */}
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-gray-700">
                    Date of Issue / Incident <span className="text-red-500">*</span>
                  </label>
                  <p className="text-xs text-gray-500">Helps SLA & audit tracking</p>
                  <input
                    type="date"
                    name="issueDate"
                    value={formData.issueDate}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                  />
                </div>

                {/* Declaration & Consent Checkbox */}
                <div className="space-y-1 pt-2">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={(e) => setFormData(prev => ({ ...prev, consent: e.target.checked }))}
                      required
                      className="mt-1 w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">
                      I confirm that the above information is true and authorize the company to contact me regarding this grievance. <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={!otpVerified || !formData.consent}
                  className={`w-full py-2.5 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 ${otpVerified && formData.consent
                    ? 'bg-primary-600 text-white hover:bg-primary-700 shadow-lg hover:shadow-xl'
                    : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  Submit Grievance
                  <ArrowRight className="w-5 h-5" />
                </button>
              </form>
            </motion.div>
          </motion.div>
        )
        }
      </AnimatePresence >
    </div >
  );
};

export default GrievanceRedressal;